import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { PlayerMatchesApiService } from '../core/player-matches-api.service';
import { PlayerReportableMatchPayload } from '../core/player-matches-api.types';
import { SessionService } from '../core/session.service';

@Component({
  selector: 'app-player-matches-page',
  standalone: true,
  imports: [CommonModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <header class="head">
        <div>
          <h1>{{ t('player.matches.title') }}</h1>
          <p class="subtitle">{{ t('player.matches.subtitle') }}</p>
        </div>
        <div class="head-actions">
          <a class="cta cta--secondary" routerLink="/tournaments">{{ t('player.matches.openTournaments') }}</a>
        </div>
      </header>

      @if (loading()) {
        <p class="state">{{ t('player.matches.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (matches().length === 0) {
        <article class="empty">
          <p><strong>{{ t('player.matches.emptyTitle') }}</strong></p>
          <p class="state">{{ t('player.matches.emptySubtitle') }}</p>
          <p><a routerLink="/tournaments">{{ t('player.matches.emptyCta') }}</a></p>
        </article>
      } @else {
        <ul class="list">
          @for (entry of matches(); track trackMatch(entry)) {
            <li class="card">
              <div class="card-head">
                <div>
                  <p class="tournament-name">{{ entry.tournament.name }}</p>
                  <p class="meta">
                    {{ entry.match.round_title }} · {{ t('player.matches.matchNumber', { value: entry.match.match_number }) }}
                  </p>
                </div>
                <span class="badge">{{ scoreReportStatusLabel(entry) }}</span>
              </div>

              <div class="teams">
                @for (team of entry.teams; track team.id) {
                  <div class="team-row">
                    <span class="slot">{{ team.slot_number }}</span>
                    <span class="team-name">{{ team.name }}</span>
                  </div>
                }
              </div>

              <div class="meta-row">
                <span>{{ t('player.matches.startsAt', { value: (entry.tournament.starts_at | date: 'short') ?? '-' }) }}</span>
              </div>

              <div class="actions">
                <a [routerLink]="['/tournaments', entry.tournament.slug, 'bracket']" [queryParams]="backQueryParams">
                  {{ t('player.matches.viewBracket') }}
                </a>
                <a class="cta" [routerLink]="['/tournaments', entry.tournament.slug, 'matches', entry.match.id, 'score-report']" [queryParams]="backQueryParams">
                  {{ t('player.matches.reportScore') }}
                </a>
              </div>
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 960px; margin: 0 auto; }
    .head { display: flex; align-items: center; justify-content: space-between; gap: 0.8rem; margin-bottom: 0.85rem; }
    .head-actions { display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap; justify-content: end; }
    h1 { margin: 0; font-size: 1.45rem; color: var(--text-strong); }
    .subtitle { margin: 0.25rem 0 0; color: var(--text-muted); }
    .cta { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.5rem 0.75rem; text-decoration: none; color: #091325; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); font-weight: 700; }
    .cta--secondary { color: #d8e8ff; background: rgba(101, 145, 216, 0.18); border-color: rgba(112, 164, 255, 0.42); }
    .list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; }
    .card, .empty { border: 1px solid var(--line-soft); border-radius: 12px; padding: 0.8rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .card-head { display: flex; justify-content: space-between; align-items: start; gap: 0.8rem; margin-bottom: 0.55rem; }
    .tournament-name { margin: 0; color: var(--text-strong); font-weight: 700; font-size: 1.02rem; }
    .meta { margin: 0.2rem 0 0; color: var(--text-muted); }
    .badge { border: 1px solid rgba(138, 184, 255, 0.45); border-radius: 999px; padding: 0.2rem 0.55rem; color: #dce9ff; font-size: 0.78rem; white-space: nowrap; }
    .teams { display: grid; gap: 0.45rem; margin: 0.65rem 0; }
    .team-row { display: flex; align-items: center; gap: 0.55rem; border: 1px solid rgba(124, 157, 211, 0.22); border-radius: 10px; padding: 0.5rem 0.6rem; background: rgba(10, 18, 31, 0.34); }
    .slot { width: 1.7rem; height: 1.7rem; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(112, 164, 255, 0.16); color: #dce9ff; font-size: 0.8rem; font-weight: 700; }
    .team-name { color: var(--text-strong); font-weight: 600; }
    .meta-row { color: var(--text-muted); font-size: 0.88rem; margin-bottom: 0.65rem; }
    .actions { display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap; }
    .actions a { text-decoration: none; color: var(--accent); font-weight: 700; }
    .actions .cta { color: #091325; }
    .state { color: var(--text-muted); }
    .state--error { color: var(--danger); }
    @media (max-width: 720px) {
      .head, .card-head { flex-direction: column; align-items: start; }
    }
  `
})
export class PlayerMatchesPageComponent {
  private readonly api = inject(PlayerMatchesApiService);
  private readonly i18n = inject(I18nService);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly matches = signal<PlayerReportableMatchPayload[]>([]);
  protected readonly loading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly backQueryParams = { from: 'my-matches' };

  constructor() {
    this.loadMatches();
  }

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  protected trackMatch(entry: PlayerReportableMatchPayload): string {
    return `${entry.tournament.slug}:${entry.match.id}`;
  }

  protected scoreReportStatusLabel(entry: PlayerReportableMatchPayload): string {
    const status = entry.score_report?.status;
    if (status === 'reported') return this.t('player.matches.status.reported');
    if (status === 'in_review') return this.t('player.matches.status.inReview');
    if (status === 'rejected') return this.t('player.matches.status.rejected');
    return this.t('player.matches.status.ready');
  }

  private loadMatches(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.api.getReportableMatches()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ matches }) => {
          this.matches.set(matches);
          this.loading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.session.clearToken();
            void this.router.navigateByUrl('/player/login');
            return;
          }

          this.errorMessage.set(this.t('player.matches.loadError'));
          this.loading.set(false);
        }
      });
  }
}
